import React from 'react';
import Form from '../../../components/form/Form';
import Swal from 'sweetalert2';
import { Button, Input, Select, Checkbox, Icon, Message } from 'semantic-ui-react';
import { comunidadProcess, eventSlugnameTrim } from '../../../components/helpers';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from '../../../components/helpers/recaptchaUtils';
import styled from '@emotion/styled';
import { screenSizes } from 'src/assets/styles/Literals';
import experienceService from '../../../services/api/experienceService';

const Page = styled.div`
  width: 90%;
  max-width: ${screenSizes.desktop};
  margin: 0 auto;
  padding: 1.5rem 1rem 2.5rem;
  box-sizing: border-box;

  @media only screen and (max-width: ${screenSizes.tablet}) {
    width: 100%;
    padding: 1rem 0.75rem 2rem;
  }
`;

const FormCard = styled.form`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  padding: 2rem;
  text-align: left;

  @media only screen and (max-width: ${screenSizes.tablet}) {
    padding: 1.25rem 1rem;
    border-radius: 10px;
  }

  h1 {
    color: #2185d0;
    font-size: 1.75rem;
    font-weight: 600;
    text-align: center;
    margin: 0 0 1.25rem;
    line-height: 1.3;
  }

  h2 {
    color: #2c3e50;
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0 0 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #2185d0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  h3 {
    color: #2c3e50;
    font-size: 1.05rem;
    font-weight: 600;
    margin: 0 0 0.35rem;
  }

  .intro {
    background: linear-gradient(135deg, #f0f7fc 0%, #f8f9fa 100%);
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.75rem;

    p {
      margin: 0 0 0.75rem;
      color: #444;
      line-height: 1.6;
      text-align: justify;
      font-size: 0.95rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .section {
    margin-bottom: 1.75rem;

    &:last-of-type {
      margin-bottom: 0;
    }

    .section-lead {
      color: #666;
      margin: 0 0 1rem;
      font-size: 0.9rem;
      line-height: 1.45;
    }

    .section-hint {
      color: #888;
      margin: 0 0 0.5rem;
      font-size: 0.85rem;
      font-style: italic;
    }
  }

  .ccf-form {
    max-width: 100%;
  }

  .form-field {
    margin-bottom: 1rem;

    label.field-label {
      display: block;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 0.4rem;
      font-size: 0.95rem;
      line-height: 1.4;
    }

    .ui.selection.dropdown,
    .ui.input {
      width: 100% !important;
      display: block !important;
      margin: 0 !important;
    }

    .ui.selection.dropdown.error,
    .ui.input.error > input {
      border-color: #e0b4b4 !important;
      background: #fff6f6 !important;
    }
  }

  .group-toggle {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 1rem 1.25rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem 1.25rem;

    .toggle-copy {
      flex: 1;
      min-width: 200px;

      h3 {
        margin-bottom: 0.25rem;
      }

      p {
        margin: 0;
        color: #666;
        font-size: 0.85rem;
        line-height: 1.4;
      }
    }

    .toggle-control {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-shrink: 0;

      .toggle-label {
        font-size: 0.9rem;
        color: #555;
        cursor: default;
        padding: 0;
      }
    }
  }

  .leader-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin-top: 0.75rem;

    @media only screen and (max-width: ${screenSizes.tablet}) {
      grid-template-columns: 1fr;
    }

    .ui.input {
      width: 100% !important;
    }
  }

  .legal {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 1rem 1.25rem;
    margin: 1.5rem 0;

    p.habeasdata {
      font-size: 0.8rem;
      line-height: 1.55;
      color: #555;
      text-align: justify;
      margin: 0 0 0.75rem;
    }

    p.habeasdata-normal {
      font-size: 0.85rem;
      color: #444;
      margin: 0;
      line-height: 1.5;

      a {
        color: #2185d0;
      }
    }
  }

  .form-errors {
    margin: 1rem 0 !important;
  }

  .submit-note {
    color: #555;
    font-size: 0.9rem;
    line-height: 1.55;
    margin: 1rem 0 1.25rem;
    padding: 0.75rem 1rem;
    background: #fff8e6;
    border-left: 4px solid #f0ad4e;
    border-radius: 0 6px 6px 0;
  }

  .submit-actions {
    text-align: center;
    padding-top: 0.5rem;

    .ui.button.primary {
      min-width: 12rem;
      font-size: 1.05rem;
      padding: 0.85rem 2rem;
      border-radius: 8px;
    }
  }

  .success-view {
    text-align: center;
    padding: 1rem 0 0.5rem;

    h1 {
      margin-bottom: 1rem;
    }

    p {
      color: #555;
      line-height: 1.6;
      margin: 0 0 1.25rem;
      max-width: 36rem;
      margin-left: auto;
      margin-right: auto;
    }

    .ui.button {
      margin-bottom: 1rem;
      border-radius: 8px;
      padding: 0.85rem 1.75rem;
    }
  }
`;

const NewVolunteersForm = () => {
  const [Attendant, set_Attendant] = React.useState({});
  const [errorInputs, set_errorInputs] = React.useState({});
  const [sent, set_sent] = React.useState(false);
  const [sending, set_sending] = React.useState(false);
  const [image, set_image] = React.useState(null);
  const getCaptchaToken = useRecaptchaToken('volunteer_form');
  const captchaRequired = isCaptchaRequired();

  const verifyForm = () => {
    let mandatoryInputs = [
      'name',
      'lastName',
      'identification',
      'phone',
      'email',
      'gender',
      'birthdate',
      'crecerlevel',
      'civil',
      'ocupation',
    ];

    if (Attendant.haveAGroup) {
      mandatoryInputs = [...mandatoryInputs, 'leaderName', 'leaderPhone'];
    }

    let inputsWithErrors = {};

    mandatoryInputs.forEach((input) => {
      inputsWithErrors = {
        ...inputsWithErrors,
        [input]: !Boolean(Attendant[input]),
      };
    });

    if (Attendant['identification'] && Attendant['identification'].length <= 5) {
      inputsWithErrors = {
        ...inputsWithErrors,
        identification: !Boolean(Attendant?.identification?.length >= 5),
      };
    }

    if (Attendant['crecerlevel']) {
      inputsWithErrors = {
        ...inputsWithErrors,
        crecerlevel: !Boolean(Attendant['crecerlevel']?.length > 0),
      };
    }

    if (Attendant['phone'] && Attendant['phone'].length <= 10) {
      inputsWithErrors = {
        ...inputsWithErrors,
        phone: !Boolean(Attendant['phone'].length >= 10),
      };
    }

    inputsWithErrors = {
      ...inputsWithErrors,
      image: !image,
    };

    set_errorInputs(inputsWithErrors);
    return Object.values(inputsWithErrors).includes(true);
  };

  const sendForm = async (e) => {
    e.preventDefault();
    if (verifyForm()) return;
    set_sending(true);

    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_sending(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    const data = new FormData();
    data.append('birthdate', Attendant.birthdate);
    data.append('civil', Attendant.civil);
    data.append('crecerlevel', Attendant.crecerlevel);
    data.append('email', Attendant.email);
    data.append('gender', Attendant.gender);
    data.append('identification', String(Attendant.identification));
    data.append('lastName', Attendant.lastName);
    data.append('leaderName', Attendant.leaderName || '');
    data.append('leaderPhone', Attendant.leaderPhone || '');
    data.append('name', Attendant.name);
    data.append('ocupation', Attendant.ocupation);
    data.append('phone', Attendant.phone);
    data.append('captcha', captcha);
    data.append('profile', image ? image : null);
    data.append('emergencyContactName', Attendant.emergencyContactName);
    data.append('emergencyContactPhone', Attendant.emergencyContactPhone);
    data.append('emergencyContactRelation', Attendant.emergencyContactRelation);

    experienceService
      .newVolunteersForm(data)
      .then((response) => {
        set_sending(false);
        if (response.data.success) {
          set_sent(true);
          Swal.fire(response?.data?.message);
        } else {
          Swal.fire(response?.data?.message || 'Hubo un error, por favor intenta más tarde.');
        }
      })
      .catch((error) => {
        set_sending(false);
        Swal.fire(error?.response?.data?.message || 'Hubo un error, por favor intenta más tarde.');
      });
  };

  const handleInputs = (event) => {
    const { name, value } = event.target;
    set_Attendant({ ...Attendant, [name]: value });
    set_errorInputs((prev) => ({ ...prev, [name]: false }));
  };

  const selectHandle = (a, { value, name }) => {
    handleInputs({ target: { value, name } });
  };

  const hasErrors = Object.values(errorInputs).includes(true);

  return (
    <Page className="new-member">
      <FormCard onSubmit={sendForm}>
        {!sent ? (
          <>
            <h1>Formulario para servir en Experiencia</h1>

            <div className="intro">
              <p>
                ¡Felicitaciones, has culminado con los dos primeros Pasos de Crecer, asistes un
                Grupo fielmente y estás listo para servir en Experiencia!
              </p>
              <p>
                Es una bendición que has dispuesto de este tiempo para que podamos servir juntos a
                Jesús. Nos encantaría que puedas llenar esta encuesta, conocerte más y saber
                aprovechar esos talentos que Dios depositó en ti. Creemos que es importante como
                personas creadas con propósito, estemos en el lugar correcto, y con las personas
                correctas.
              </p>
              <p>
                Recuerda contestar cada pregunta con total sinceridad, no existen respuestas buenas
                o malas.
              </p>
            </div>

            <section className="section">
              <h2>
                <Icon name="user" />
                ¡Queremos conocerte!
              </h2>
              <p className="section-lead">
                Llena el siguiente formulario con tus datos personales.
              </p>
              <Form
                Attendant={Attendant}
                handleInputs={handleInputs}
                errorInputs={errorInputs}
                showOcupation
                imageFile={image}
                set_image={set_image}
                showGender
                showCivil
                showEmergencyContact
                showBirthdate
                setAttendant={set_Attendant}
                setErrorInputs={set_errorInputs}
              />
            </section>

            <section className="section">
              <h2>
                <Icon name="graduation cap" />
                Tu proceso en Comunidad
              </h2>
              <div className="form-field">
                <label className="field-label" htmlFor="crecerlevel">
                  ¿Qué cursos has culminado de Comunidad Crecer?
                </label>
                <p className="section-hint">Puedes seleccionar varios</p>
                <Select
                  search
                  placeholder="Selecciona los cursos que has culminado"
                  onChange={selectHandle}
                  value={Attendant.crecerlevel || ''}
                  name="crecerlevel"
                  id="crecerlevel"
                  className={`Registration__input Registration__input-crecerlevel ${
                    errorInputs.crecerlevel ? 'error' : ''
                  }`}
                  fluid
                  multiple
                  selection
                  options={comunidadProcess.map((opt) => ({
                    key: opt,
                    value: opt,
                    text: eventSlugnameTrim(opt),
                  }))}
                />
              </div>

              <div className="group-toggle">
                <div className="toggle-copy">
                  <h3>¿Estás conectado con un Grupo?</h3>
                  <p>Asistes a un grupo de manera frecuente y tienes un líder.</p>
                </div>
                <div className="toggle-control">
                  <span className="toggle-label">No asisto a un grupo</span>
                  <Checkbox
                    label="Sí, asisto a un grupo"
                    id="haveAGroup"
                    name="haveAGroup"
                    checked={Attendant.haveAGroup}
                    toggle
                    onClick={() =>
                      set_Attendant({ ...Attendant, haveAGroup: !Attendant.haveAGroup })
                    }
                  />
                </div>
              </div>
            </section>

            {Attendant.haveAGroup && (
              <section className="section">
                <h2>
                  <Icon name="users" />
                  Datos de tu líder
                </h2>
                <p className="section-hint">Nombre completo y celular de tu líder de grupo</p>
                <div className="leader-fields">
                  <Input
                    placeholder="Nombre del líder"
                    onChange={handleInputs}
                    value={Attendant.leaderName || ''}
                    name="leaderName"
                    id="leaderName"
                    className={`Registration__input Registration__input-leaderName ${
                      errorInputs.leaderName ? 'error' : ''
                    }`}
                  />
                  <Input
                    placeholder="Teléfono del líder"
                    onChange={handleInputs}
                    type="number"
                    value={Attendant.leaderPhone || ''}
                    name="leaderPhone"
                    id="leaderPhone"
                    className={`Registration__input Registration__input-leaderPhone ${
                      errorInputs.leaderPhone ? 'error' : ''
                    }`}
                  />
                </div>
              </section>
            )}

            <div className="legal">
              <p className="habeasdata">
                La IGLESIA COMUNIDAD CRISTIANA DE FE Y VIENTO FRESCO, con nombre distintivo:
                COMUNIDAD MDE, hará uso de la información personal suministrada por el voluntario,
                con fines de generar retroalimentación sobre toda su actividad durante su
                permanencia en la iglesia. El voluntario, se compromete a actualizarla cuando sea
                pertinente o a pedir que su información sea eliminada de nuestras bases de datos al
                momento que se encuentre desvinculado; según términos establecidos en la ley Habeas
                data 1581 de 2012. Igualmente se autoriza el uso y tratamiento de fotos y videos con
                fines comunicativos, en nuestras diferentes plataformas digitales y comunicación
                interna de la iglesia.
              </p>
              <p className="habeasdata-normal">
                Al enviar aceptas el tratamiento de datos de Comunidad Cristiana de Fe y Viento
                Fresco.
                <br />
                <small>
                  Si desea más información,{' '}
                  <a
                    href="https://www.funcionpublica.gov.co/eva/gestornormativo/norma.php?i=49981"
                    target="_blank"
                    rel="noreferrer"
                  >
                    puede hacer clic en TÉRMINOS Y CONDICIONES para ampliar la información.
                  </a>
                </small>
              </p>
            </div>

            {hasErrors && (
              <Message negative className="form-errors">
                <Message.Header>Por favor verifica los campos marcados</Message.Header>
                <p>Revisa la información ingresada e intenta enviar nuevamente.</p>
              </Message>
            )}

            <p className="submit-note">
              Importante: Cuando presiones enviar, verás un botón que te lleva a que llenes las
              encuestas para conocer más de tu perfil. Te pedimos que lo llenes lo más pronto
              posible.
            </p>

            <div className="submit-actions">
              <Button type="submit" primary disabled={sending} loading={sending}>
                Enviar
              </Button>
            </div>
          </>
        ) : (
          <div className="success-view">
            <h1>¡Gracias {Attendant.name}!</h1>
            <p>
              Te pedimos que para continuar con el proceso llenes estas encuestas de perfil y dones
              espirituales.
            </p>
            <Button
              as="a"
              href="https://info.comunidadmde.com/crecer/encuestas?surveys=disc,spiritualgifts"
              primary
            >
              Ir a las encuestas
            </Button>
            <p>Pronto nos estaremos comunicando contigo.</p>
          </div>
        )}
      </FormCard>
    </Page>
  );
};

export default NewVolunteersForm;
